import { type AssetMedia, type TechlineAsset, TechlineStatusCode } from '@packages/clients';

import type { TechlineAssetEntity, TechlineAssetMediaItem, TechlineAssetStatus } from '../entities';

import { isNonNullId } from './checks';

/**
 * Determine the case status to send with an asset upload or update request.
 * ESCALATED cases keep their status; all others use PENDING_TECHLINE.
 *
 * @param caseStatusCode - the current case status
 * @returns the status code to include in the asset request
 * @example
 * const status = mapCaseStatusToUploadStatus('ESCALATED'); // TechlineStatusCode.ESCALATED
 */
export function mapCaseStatusToUploadStatus(caseStatusCode: string): TechlineStatusCode {
    if (caseStatusCode === TechlineStatusCode.ESCALATED) {
        return TechlineStatusCode.ESCALATED;
    }
    return TechlineStatusCode.PENDING_TECHLINE;
}

/**
 * Map a `TechlineAsset` DTO from the API response to a `TechlineAssetEntity` domain entity.
 * Converts all nested media items to the unified `TechlineAssetMediaItem` shape and
 * strips API baggage (description fields, redundant IDs).
 *
 * @param dto - the raw asset object from the API
 * @returns the mapped domain entity
 * @example
 * const entity = toTechlineAssetEntity(apiAsset);
 */
export function toTechlineAssetEntity(dto: TechlineAsset): TechlineAssetEntity {
    const media = dto.media.map(toTechlineAssetMediaItem);

    return {
        assetType: dto.assetTypeId,
        contentType: resolveContentType(media),
        createdAt: dto.createdTimestamp,
        disposition: dto.assetDispositionId,
        downloadUrl: resolveDownloadUrl(media),
        error: dto.error,
        hasOverlay: dto.hasOverlay,
        id: dto.assetId,
        isImportedFromSiebel: dto.importedFromSiebel,
        isWorksheetAsset: Boolean(dto.worksheetQuestionSubmissionSourceId),
        media,
        name: dto.name,
        note: dto.note,
        progress: dto.percentUploaded != null ? dto.percentUploaded * 100 : 0,
        status: toTechlineAssetStatus(dto.assetProcessingStateId),
        thumbnailUrl: resolveThumbnailUrl(media),
        worksheetSourceId: dto.worksheetQuestionSubmissionSourceId ?? '',
    };
}

/**
 * Map a single API media item to the domain entity representation.
 * Copies all fields present on the DTO variant (image, video, or file) into the
 * unified `TechlineAssetMediaItem` shape.
 *
 * @param media - the raw media item from the API response
 * @returns the mapped domain media item
 * @example
 * const mediaItem = toTechlineAssetMediaItem(apiMedia);
 */
export function toTechlineAssetMediaItem(media: AssetMedia): TechlineAssetMediaItem {
    const base: TechlineAssetMediaItem = {
        contentType: media.contentType,
        createdTimestamp: media.createdTimestamp,
        createdTimestampDescription: media.createdTimestampDescription,
        sizeInBytes: media.sizeInBytes,
        sizeInBytesDescription: media.sizeInBytesDescription,
        url: media.url,
    };

    // Image media variant
    if ('assetImageId' in media && isNonNullId(media.assetImageId)) {
        return {
            ...base,
            assetImageId: media.assetImageId,
            assetImageTypeId: media.assetImageTypeId,
            assetImageTypeIdDescription: media.assetImageTypeIdDescription,
            height: media.height,
            heightDescription: media.heightDescription,
            width: media.width,
            widthDescription: media.widthDescription,
        };
    }

    // Video media variant
    if ('assetVideoId' in media && isNonNullId(media.assetVideoId)) {
        return {
            ...base,
            assetVideoId: media.assetVideoId,
            assetVideoTypeId: media.assetVideoTypeId,
            assetVideoTypeIdDescription: media.assetVideoTypeIdDescription,
            durationInSeconds: media.durationInSeconds,
            durationInSecondsDescription: media.durationInSecondsDescription,
            height: media.height,
            heightDescription: media.heightDescription,
            width: media.width,
            widthDescription: media.widthDescription,
        };
    }

    // File media variant
    if ('assetFileId' in media && isNonNullId(media.assetFileId)) {
        return {
            ...base,
            assetFileId: media.assetFileId,
        };
    }

    return base;
}

/**
 * Map an API processing state string to the domain entity's lowercase status.
 *
 * @param apiState - the raw processing state from the API (e.g. 'UPLOADING', 'COMPLETE')
 * @returns the corresponding domain status
 * @example
 * const status = toTechlineAssetStatus('COMPLETE'); // 'complete'
 */
export function toTechlineAssetStatus(apiState: string): TechlineAssetStatus {
    const normalized = apiState.toLowerCase();
    if (
        normalized === 'uploading' ||
        normalized === 'processing' ||
        normalized === 'complete' ||
        normalized === 'error'
    ) {
        return normalized;
    }
    return 'error';
}

/**
 * Find the content type from a mapped media array. Checks for an ORIGINAL image,
 * an ORIGINAL video, or a file media entry, in that order. Falls back to the first
 * media item's content type if none of those match. Returns an empty string when the array is empty.
 *
 * @param media - the mapped media items for an asset
 * @returns the content type, or an empty string if unavailable
 */
function resolveContentType(media: TechlineAssetMediaItem[]): string {
    const image = media.find((m) => m.assetImageTypeId === 'ORIGINAL');
    if (image?.contentType) {
        return image.contentType;
    }

    // Then the ORIGINAL video variant
    const video = media.find((m) => m.assetVideoTypeId === 'ORIGINAL');
    if (video?.contentType) {
        return video.contentType;
    }

    const file = media.find((m) => Boolean(m.assetFileId));
    if (file?.contentType) {
        return file.contentType;
    }

    return media[0]?.contentType ?? '';
}

/**
 * Find the best download URL from a mapped media array. Checks for an ORIGINAL image,
 * an ORIGINAL video, or a file media entry, in that order. Falls back to the first
 * media item's URL if none of those match. Returns an empty string when the array is empty.
 *
 * @param media - the mapped media items for an asset
 * @returns the download URL, or an empty string if unavailable
 */
function resolveDownloadUrl(media: TechlineAssetMediaItem[]): string {
    // Prefer the ORIGINAL image variant
    const image = media.find((m) => m.assetImageTypeId === 'ORIGINAL');
    if (image?.url) {
        return image.url;
    }

    // Then the ORIGINAL video variant
    const video = media.find((m) => m.assetVideoTypeId === 'ORIGINAL');
    if (video?.url) {
        return video.url;
    }

    // Then any file media entry
    const file = media.find((m) => Boolean(m.assetFileId));
    if (file?.url) {
        return file.url;
    }

    // Last resort: first media item
    return media[0]?.url ?? '';
}

/**
 * Find the thumbnail URL from a mapped media array. Checks for a THUMBNAIL image variant.
 *
 * @param media - the mapped media items for an asset
 * @returns the thumbnail URL, or undefined if unavailable
 */
function resolveThumbnailUrl(media: TechlineAssetMediaItem[]): string | undefined {
    return media.find((m) => m.assetImageTypeId === 'THUMBNAIL')?.url;
}
